/**
 * Predictive search drawer — sections/search-drawer.liquid.
 *
 * Everything here is progressive enhancement over markup that already works: the bar
 * is a real GET form to the results page, every row and card is a real link, and the
 * first paint is the drawer's idle state. With the script off, the search icon still
 * navigates to /search.
 *
 * Typing fetches `/search/suggest?…&section_id=<this section>` and swaps only
 * `[data-search-results]` out of the response. The form is never replaced, so the
 * caret, the selection and the composition state all survive a keystroke landing
 * mid-request.
 */
(() => {
  const DEBOUNCE_MS = 250;
  /* A settings change replaces this element and re-runs this file, so whether the drawer
     was open cannot live on the instance or in this closure. Editor-only. */
  const EDITOR_OPEN = '__searchDrawerEditorOpen';
  const RESOURCE_TYPES = 'product,page,query';
  // The API's per-type maximum, so a list block set above its six-row default still has
  // rows to render; each block trims the response to its own setting.
  const RESOURCE_LIMIT = 10;

  class SearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onEditorDeselect = this.onEditorDeselect.bind(this);
      this.onEditorLoad = this.onEditorLoad.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-search-dialog]');
      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.closeButton = this.querySelector('[data-search-close]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');

      if (!this.dialog || !this.input || !this.results) return;

      this.sectionId = this.dataset.sectionId;
      this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      this.countUrl = this.dataset.countUrl;
      this.minLength = Number.parseInt(this.dataset.minLength, 10) || 2;

      // The pre-type markup, kept so clearing the box restores it without a round trip.
      this.idleHTML = this.results.innerHTML;
      this.isIdle = true;

      this.debounceTimer = null;
      this.controller = null;

      this.input.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      this.clearButton?.addEventListener('click', () => this.clear());
      this.closeButton?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('close', () => this.onDialogClose());

      // The trigger lives in the header section, so it is reached from the document
      // rather than from inside this element.
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.onResize);

      // A closed dialog has nothing to select in the theme editor, so the drawer
      // follows the editor's own selection instead of the search icon.
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.addEventListener('shopify:block:deselect', this.onEditorDeselect);
        // Every settings change re-renders the section, which replaces this element with
        // a fresh one whose dialog is closed. Without this the drawer vanishes the moment
        // a merchant edits anything and does not come back until they reselect it.
        document.addEventListener('shopify:section:load', this.onEditorLoad);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      document.removeEventListener('shopify:block:deselect', this.onEditorDeselect);
      document.removeEventListener('shopify:section:load', this.onEditorLoad);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.debounceTimer);
      this.controller?.abort();
    }

    /* ---- Theme editor ---------------------------------------------------- */

    // True for this section's own event and for any block inside it. The section
    // event's target is the `.shopify-section` wrapper, which contains this element;
    // a block event's target is inside it.
    isOurs(event) {
      const target = event.target;
      if (!(target instanceof Element)) return false;
      return target.contains(this) || this.contains(target);
    }

    onEditorSelect(event) {
      if (!this.isOurs(event)) return;
      this.open();
      this.revealGroupFor(event.target);
    }

    // Fires on the freshly rendered section, so this instance is the new one. Re-opened
    // only if the merchant had it open: a section that re-renders while it is not the
    // selection gets no deselect afterwards, so an unasked-for drawer would stay up.
    onEditorLoad(event) {
      if (this.isOurs(event) && window[EDITOR_OPEN]) this.open();
    }

    // Every state group is in the DOM in the editor but only the previewed one is shown,
    // so selecting a Search ad while previewing the results state would highlight
    // something invisible. Show the group the selection actually lives in.
    revealGroupFor(target) {
      const group = target.closest?.('[data-state-group]');
      if (!group || !group.hidden) return;
      this.querySelectorAll('[data-state-group]').forEach((el) => {
        el.hidden = el !== group;
      });
      // Its track measured zero while hidden, so `evaluate` skipped it — and the drawer
      // was already open, so `open()` returned before mounting anything.
      this.mountCarousels();
    }

    onEditorDeselect(event) {
      if (this.isOurs(event)) this.close();
    }

    /* ---- Open / close --------------------------------------------------- */

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-header-search]');
      if (!trigger) return;
      // The trigger is an anchor to /search — that is the no-JS path, so only take it
      // over for a plain left click.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.open();
    }

    open() {
      if (this.dialog.open) return;

      this.positionToHeader();

      // Non-modal in the editor: `showModal()` makes the rest of the preview inert, so
      // a merchant could no longer click another section to move on. It also skips the
      // focus grab, which would fight the settings panel they are typing in.
      if (window.Shopify?.designMode) {
        this.dialog.show();
        this.setAttribute('data-open', 'true');
        window[EDITOR_OPEN] = true;
        this.mountCarousels();
        return;
      }

      this.dialog.showModal();
      this.setAttribute('data-open', 'true');
      this.mountCarousels();
      // Caret to the end rather than a select-all: reopening with a previous term
      // should read as continuing, not as replacing.
      const value = this.input.value;
      this.input.focus();
      this.input.setSelectionRange?.(value.length, value.length);
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    // The drawer hangs off the top of the header's card, not the top of the viewport.
    // That edge moves with the announcement bar and with the sticky state, so it is
    // measured rather than assumed; the stylesheet's fallback covers a missing header.
    positionToHeader() {
      const bar = document.querySelector('.header__bar');
      if (!bar) return;
      const top = Math.max(0, Math.round(bar.getBoundingClientRect().top));
      this.style.setProperty('--search-drawer-top', `${top}px`);
    }

    // Scrolling is locked while the drawer is open, so a resize is the only thing that
    // can move the header out from under it.
    onResize() {
      if (this.dialog?.open) this.positionToHeader();
    }

    // Fires for the close button, Esc and a backdrop dismissal alike. `showModal`
    // returns focus to the trigger on its own.
    onDialogClose() {
      this.removeAttribute('data-open');
      window[EDITOR_OPEN] = false;
      window.clearTimeout(this.debounceTimer);
      this.controller?.abort();
      this.controller = null;
      this.removeAttribute('data-loading');
    }

    /* ---- Query ---------------------------------------------------------- */

    onInput() {
      const term = this.input.value.trim();
      this.toggleClear(term.length > 0);

      window.clearTimeout(this.debounceTimer);

      if (term.length < this.minLength) {
        this.controller?.abort();
        this.controller = null;
        this.removeAttribute('data-loading');
        this.restoreIdle();
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.fetchResults(term), DEBOUNCE_MS);
    }

    clear() {
      this.input.value = '';
      this.toggleClear(false);
      window.clearTimeout(this.debounceTimer);
      this.controller?.abort();
      this.controller = null;
      this.removeAttribute('data-loading');
      this.restoreIdle();
      this.input.focus();
    }

    toggleClear(visible) {
      if (!this.clearButton) return;
      this.clearButton.hidden = !visible;
    }

    /* A closed `<dialog>` measures a zero-width track, which carousel.js reads as "does
       not overflow" and skips — so the tile row can only mount once the drawer is open,
       and again after every swap, because replaced markup arrives unmounted. */
    mountCarousels() {
      requestAnimationFrame(() => window.themeCarousel?.refresh(this));
    }

    /* The other half of that contract: a row replaced while still mounted leaves its
       Splide instance and overflow observer behind, firing against detached markup. */
    teardownCarousels() {
      this.results.querySelectorAll('.splide[data-carousel]').forEach((el) => {
        window.themeCarousel?.destroy?.(el);
      });
    }

    restoreIdle() {
      // Typing the first character of a new query lands here with the idle markup
      // already up; swapping it again would reset the tile row's scroll.
      if (this.isIdle) return;
      this.teardownCarousels();
      this.results.innerHTML = this.idleHTML;
      this.isIdle = true;
      this.setState('idle');
      this.announce('');
      this.mountCarousels();
    }

    async fetchResults(term) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('data-loading', 'true');
      this.removeAttribute('data-error');

      const url = new URL(this.suggestUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', RESOURCE_TYPES);
      url.searchParams.set('resources[limit]', String(RESOURCE_LIMIT));
      url.searchParams.set('resources[limit_scope]', 'each');
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);

      // Sent alongside the suggestions rather than after them, so the heading is corrected
      // as soon as the total lands. The rejection is settled here: nothing awaits this
      // promise until the swap, and an abort before then would otherwise go unhandled.
      const pendingTotal = this.fetchTotal(term, controller.signal).catch(() => null);

      try {
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search responded ${response.status}`);

        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const fresh = doc.querySelector('[data-search-results]');
        const root = doc.querySelector('search-drawer');
        if (!fresh) throw new Error('Predictive search response had no results region');

        this.teardownCarousels();
        this.results.innerHTML = fresh.innerHTML;
        this.isIdle = false;
        this.setState(root?.dataset.state || 'results');
        this.mountCarousels();
        this.announceCount();
        this.applyTotal(pendingTotal, controller);
      } catch (error) {
        // An abort is the expected outcome of typing faster than the network. A real
        // failure leaves the previous results up and marks the element for QA.
        if (error.name === 'AbortError') return;
        this.setAttribute('data-error', 'true');
      } finally {
        // Identity, not the abort flag: an older request reaching here after a newer
        // keystroke replaced it would otherwise clear the flag mid-flight.
        if (this.controller === controller) this.removeAttribute('data-loading');
      }
    }

    /* The heading is rendered with the number of cards the drawer is showing, because the
       predictive API carries no total. templates/search.count.liquid runs the same query
       for the store's real match count, as a bare number under `layout none`. */
    async fetchTotal(term, signal) {
      if (!this.countUrl) return null;

      const url = new URL(this.countUrl, window.location.origin);
      url.searchParams.set('q', term);

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return null;

      const total = Number.parseInt((await response.text()).trim(), 10);
      return Number.isNaN(total) ? null : total;
    }

    applyTotal(pending, controller) {
      pending.then((total) => {
        // A total that resolved after the next keystroke took over would be written onto
        // markup it does not describe, so it is dropped unless its request is still the
        // current one. A failed count leaves the rendered number standing.
        if (total === null || this.controller !== controller) return;
        const node = this.results.querySelector('[data-search-count]');
        if (node) node.textContent = total;
      });
    }

    setState(state) {
      this.dataset.state = state;
      this.input.setAttribute('aria-expanded', state === 'idle' ? 'false' : 'true');
    }

    announceCount() {
      const count = this.results.querySelectorAll('[data-search-link], .search-card').length;
      // The template is rendered with a sentinel rather than a number, so the string
      // stays translatable and the count is filled in here.
      const template = this.status?.dataset.template;
      this.announce(template ? template.replace('%%COUNT%%', count) : String(count));
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    /* ---- Keyboard ------------------------------------------------------- */

    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      // The card's media link is aria-hidden and out of the tab order, so the arrows skip
      // it too and land on the title link that names the same product.
      const items = Array.from(
        this.results.querySelectorAll(
          'a[href]:not([tabindex="-1"]):not([aria-hidden="true"]), button:not([disabled])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;

      event.preventDefault();

      const current = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        // From the input, or past the end, the first row is next.
        items[current < 0 || current === items.length - 1 ? 0 : current + 1].focus();
        return;
      }

      // Up off the first row returns to the box, which is where the customer is typing.
      if (current <= 0) this.input.focus();
      else items[current - 1].focus();
    }
  }

  if (!customElements.get('search-drawer')) {
    customElements.define('search-drawer', SearchDrawer);
  }
})();
